/**
 * ViolationAdvancedSettings - SLA targets, Escalation Rules, Webhooks,
 * QR Code toggle, Survey toggle for the Violations module.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Chip, IconButton, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
  Switch, FormControlLabel, Divider,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon   from "@mui/icons-material/Edit";
import SaveIcon   from "@mui/icons-material/Save";
import api from "../../utils/api";

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

// ── SLA per violation type ─────────────────────────────────────────
const ViolationSLASettings = () => {
  const [types,  setTypes]  = useState([]);
  const [saving, setSaving] = useState({});
  const [snack,  setSnack]  = useState({ open:false, msg:"", sev:"success" });

  useEffect(() => { api.get("/getCcrViolationTypes").then(r => setTypes(r.data||[])).catch(()=>{}); }, []);

  const handleSave = async (type) => {
    setSaving(p=>({...p,[type.id]:true}));
    try {
      await api.put(`/violation-types/${type.id}/sla`, { sla_days: type.sla_days });
      setSnack({ open:true, msg:`SLA updated for "${type.title}".`, sev:"success" });
    } catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
    finally { setSaving(p=>({...p,[type.id]:false})); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Set a target number of days for each violation type to be resolved. Violations exceeding 80% will show a warning.
      </Typography>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>{["Violation Type","SLA (days)","Action"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {types.map(t=>(
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{t.title}</TableCell>
                <TableCell>
                  <TextField type="number" size="small" value={t.sla_days||""} placeholder="No SLA"
                    onChange={e => setTypes(prev=>prev.map(x=>x.id===t.id?{...x,sla_days:e.target.value?parseInt(e.target.value,10):null}:x))}
                    inputProps={{ min:1, max:365 }} sx={{ ...fieldSx, width:120 }} />
                </TableCell>
                <TableCell>
                  <Button size="small" startIcon={saving[t.id]?<CircularProgress size={12}/>:<SaveIcon fontSize="small"/>}
                    onClick={()=>handleSave(t)} sx={{ ...btnSx, py:0.5 }}>Save</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Escalation Rules ───────────────────────────────────────────────
const EscalationRules = () => {
  const [rules,   setRules]   = useState([]);
  const [types,   setTypes]   = useState([]);
  const [form,    setForm]    = useState({ name:"", violation_type_id:"", trigger_days:30, action_type:"send_letter", auto_status:"", fine_amount:"", sort_order:0 });
  const [editing, setEditing] = useState(null);
  const [snack,   setSnack]   = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    const [rr, ty] = await Promise.all([api.get("/violation-escalation-rules"), api.get("/getCcrViolationTypes")]);
    setRules(rr.data||[]); setTypes(ty.data||[]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name || !form.trigger_days) { setSnack({ open:true, msg:"Name and trigger days required.", sev:"error" }); return; }
    try {
      if (editing) await api.put(`/violation-escalation-rules/${editing}`, form);
      else         await api.post("/violation-escalation-rules", form);
      setForm({ name:"", violation_type_id:"", trigger_days:30, action_type:"send_letter", auto_status:"", fine_amount:"", sort_order:0 });
      setEditing(null); setSnack({ open:true, msg:"Saved.", sev:"success" }); load();
    } catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Define automatic actions triggered after a violation has been open for a set number of days.
        Rules run nightly and apply in order.
      </Typography>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>{editing?"Edit Rule":"Add Escalation Rule"}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Rule Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Violation Type" value={form.violation_type_id} onChange={e=>setForm(f=>({...f,violation_type_id:e.target.value}))} sx={fieldSx}><MenuItem value="">All types</MenuItem>{types.map(t=><MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField type="number" fullWidth size="small" label="Trigger after (days) *" value={form.trigger_days} onChange={e=>setForm(f=>({...f,trigger_days:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField select fullWidth size="small" label="Action" value={form.action_type} onChange={e=>setForm(f=>({...f,action_type:e.target.value}))} sx={fieldSx}>{["send_letter","change_status","create_fine","send_email"].map(a=><MenuItem key={a} value={a}>{a.replace(/_/g," ")}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Auto-set Status" value={form.auto_status} onChange={e=>setForm(f=>({...f,auto_status:e.target.value}))} sx={fieldSx} helperText="Optional status change" /></Grid>
          <Grid item xs={12} sm={3}><TextField type="number" fullWidth size="small" label="Fine Amount ($)" value={form.fine_amount} onChange={e=>setForm(f=>({...f,fine_amount:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField type="number" fullWidth size="small" label="Sort Order" value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}>
            <Box sx={{ display:"flex", gap:1 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={btnSx}>{editing?"Update":"Add Rule"}</Button>
              {editing && <Button variant="outlined" onClick={()=>{ setEditing(null); setForm({ name:"", violation_type_id:"", trigger_days:30, action_type:"send_letter", auto_status:"", fine_amount:"", sort_order:0 }); }} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["Rule","Type","Trigger","Action","Auto Status","Fine",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rules.map(r=>(
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{r.name}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{r.type_name||"All"}</TableCell>
                <TableCell><Chip size="small" label={`${r.trigger_days} days`} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} /></TableCell>
                <TableCell sx={{ fontSize:12 }}>{r.action_type.replace(/_/g," ")}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{r.auto_status||"N/A"}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{r.fine_amount ? `$${r.fine_amount}` : "N/A"}</TableCell>
                <TableCell>
                  <Box sx={{ display:"flex", gap:0.5 }}>
                    <IconButton size="small" onClick={()=>{ setEditing(r.id); setForm({ name:r.name, violation_type_id:r.violation_type_id||"", trigger_days:r.trigger_days, action_type:r.action_type, auto_status:r.auto_status||"", fine_amount:r.fine_amount||"", sort_order:r.sort_order||0 }); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/violation-escalation-rules/${r.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!rules.length && <TableRow><TableCell colSpan={7} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No escalation rules configured.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Webhooks ───────────────────────────────────────────────────────
const ViolationWebhooks = () => {
  const [hooks, setHooks] = useState([]);
  const [form,  setForm]  = useState({ url:"", events:"status_change", secret_key:"" });
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });
  const load = useCallback(async () => { const r = await api.get("/violation-webhooks"); setHooks(r.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const handleAdd = async () => {
    if (!form.url) { setSnack({ open:true, msg:"URL required.", sev:"error" }); return; }
    try { await api.post("/violation-webhooks", form); setForm({ url:"", events:"status_change", secret_key:"" }); setSnack({ open:true, msg:"Added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };
  return (
    <Box>
      <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>Send HTTP POST notifications to external systems when violation events occur.</Alert>
      <Paper elevation={0} sx={cardSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Endpoint URL" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Events" value={form.events} onChange={e=>setForm(f=>({...f,events:e.target.value}))} sx={fieldSx} helperText="status_change, comment_added, *" /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Secret" value={form.secret_key} onChange={e=>setForm(f=>({...f,secret_key:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add Webhook</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["URL","Events","Active",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {hooks.map(h=>(
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontSize:12, maxWidth:280 }}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:260 }}>{h.url}</Typography></TableCell>
                <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{h.events}</TableCell>
                <TableCell><Chip size="small" label={h.is_active?"Active":"Off"} onClick={async()=>{ await api.patch(`/violation-webhooks/${h.id}`,{is_active:!h.is_active}); load(); }} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:h.is_active?"rgba(34,197,94,0.1)":"rgba(0,0,0,0.06)", color:h.is_active?"#16a34a":"#6b778c", cursor:"pointer" }} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/violation-webhooks/${h.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!hooks.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No webhooks configured.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Optional Features (QR + Survey) ───────────────────────────────
const OptionalFeatures = () => {
  const [settings, setSettings] = useState({ violation_qr_enabled: true, violation_survey_enabled: false });
  const [saving,   setSaving]   = useState(false);
  const [snack,    setSnack]    = useState({ open:false, msg:"", sev:"success" });

  useEffect(() => {
    api.get("/app-settings").then(r => {
      const s = r.data || {};
      setSettings({
        violation_qr_enabled: s.violation_qr_enabled !== 'false',
        violation_survey_enabled: s.violation_survey_enabled === 'true',
      });
    }).catch(()=>{});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/app-settings/violation_qr_enabled", { value: String(settings.violation_qr_enabled) }),
        api.put("/app-settings/violation_survey_enabled", { value: String(settings.violation_survey_enabled) }),
      ]);
      setSnack({ open:true, msg:"Settings saved.", sev:"success" });
    } catch { setSnack({ open:true, msg:"Failed to save.", sev:"error" }); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:3 }}>
        Enable or disable optional features for the Violations module.
      </Typography>

      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Optional Features</Typography>

        <Box sx={{ display:"flex", flexDirection:"column", gap:2 }}>
          {/* QR Code */}
          <Box sx={{ p:2, borderRadius:"10px", bgcolor: settings.violation_qr_enabled ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)", border:"1px solid rgba(47,99,173,0.08)" }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>QR Code on Notices</Typography>
                <Typography variant="caption" sx={{ color:"#6b778c" }}>
                  Generate a QR code for each violation that members can scan to check the status without logging in.
                  The QR code links to a public status page showing violation type, status, and comment (no personal data).
                </Typography>
              </Box>
              <Switch checked={settings.violation_qr_enabled} onChange={e=>setSettings(s=>({...s,violation_qr_enabled:e.target.checked}))} />
            </Box>
          </Box>

          {/* Survey */}
          <Box sx={{ p:2, borderRadius:"10px", bgcolor: settings.violation_survey_enabled ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)", border:"1px solid rgba(47,99,173,0.08)" }}>
            <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>Member Satisfaction Survey</Typography>
                <Typography variant="caption" sx={{ color:"#6b778c" }}>
                  When enabled, staff can send a 1-question satisfaction survey to the member after a violation is resolved.
                  Members rate their experience 1-5 stars and can leave a comment. Results visible in violation details.
                </Typography>
              </Box>
              <Switch checked={settings.violation_survey_enabled} onChange={e=>setSettings(s=>({...s,violation_survey_enabled:e.target.checked}))} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt:2 }}>
          <Button variant="contained" startIcon={saving?null:<SaveIcon />} onClick={handleSave} disabled={saving} sx={btnSx}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Main ───────────────────────────────────────────────────────────
const ViolationAdvancedSettings = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Advanced Settings</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>SLA targets, escalation rules, webhooks, and optional features (QR codes, surveys).</Typography>
      </Box>
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:44, fontSize:13 } }}>
          <Tab label="SLA Targets" />
          <Tab label="Escalation Rules" />
          <Tab label="Webhooks" />
          <Tab label="Optional Features" />
        </Tabs>
      </Paper>
      {tab===0 && <ViolationSLASettings />}
      {tab===1 && <EscalationRules />}
      {tab===2 && <ViolationWebhooks />}
      {tab===3 && <OptionalFeatures />}
    </Box>
  );
};

export default ViolationAdvancedSettings;
